import { useStore } from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import styled from 'styled-components';

import DeleteIcon from '../../svgs/DeleteIcon';
import Accordion from './Accordion';

const BrandKitExternalLinks = observer(() => {
  const videoCreator = useVideoCreatorStore();
  const { datoClientStore } = useStore();
  const branding = videoCreator.organization?.branding;
  const externalLinks = branding?.externalLinks;
  const [value, setValue] = useState('');

  useEffect(() => {
    if (externalLinks) setValue(externalLinks);
  }, [Boolean(externalLinks)]);

  const [isSaving, setIsSaving] = useState(false);
  const [isSaved, setIsSaved] = useState(false);
  const handleSave = async () => {
    setIsSaving(true);
    setIsSaved(false);
    const newValue = filterValues(value).trim();
    const organization = videoCreator.organization;
    organization!.branding = {
      ...organization!.branding!,
      externalLinks: newValue,
    };
    await datoClientStore.albumRepository?.update(organization!);
    setValue(newValue);
    setIsSaving(false);
    setIsSaved(true);
  };

  const handleAdd = () => {
    setValue(prev => `${prev},`);
  };

  const externalLinksList = value.split(',');
  const isChanged = filterValues(value) !== filterValues(externalLinks || '');
  const disabled = isSaving || !isChanged;

  return (
    <Accordion id="external-links" title="External Links" isSaved={isSaved}>
      <Container isSaving={isSaving}>
        {externalLinksList?.map((link, i) => (
          <ExternalLink
            key={i}
            value={link}
            onChange={linkValue =>
              setValue(curr => {
                const list = curr.split(',');
                list[i] = linkValue;
                return list.join(',');
              })
            }
            onDelete={() => {
              setValue(curr => {
                const list = curr.split(',');
                list.splice(i, 1);
                return list.join(',');
              });
            }}
          />
        ))}
        <div className="actions">
          <button className="add-btn" disabled={isSaving} onClick={handleAdd}>
            Add New
          </button>
          <button className="save-btn" disabled={disabled} onClick={handleSave}>
            {isSaving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </Container>
    </Accordion>
  );
});

const ExternalLink = ({
  value,
  onChange,
  onDelete,
}: {
  value: string;
  onChange: (value: string) => void;
  onDelete: () => void;
}) => {
  return (
    <div className="input-wrapper">
      <input
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder="Add external link (e.g. Google Drive)"
      />
      <div role="button" className="delete-icon" onClick={onDelete}>
        <DeleteIcon width="20" height="20" />
      </div>
    </div>
  );
};

const filterValues = (value: string | string[]) => {
  if (Array.isArray(value)) return value.filter(Boolean).join(',');
  return value
    .split(',')
    .map(s => s.trim())
    .filter(Boolean)
    .join(',');
};

const Container = styled.div<{ isSaving?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 24px;

  .input-wrapper {
    display: flex;
    align-items: center;
    justify-content: space-between;
    border: 1px solid var(--border);
    border-radius: 8px;
    padding-right: 24px;

    &:focus-within {
      border-color: var(--text-secondary);
    }

    > input {
      flex-grow: 1;
      color: var(--text-secondary);
      font-family: var(--font-primary);
      font-size: var(--text-sm);
      font-weight: var(--font-weight-regular);
      background: transparent;
      outline: none;
      border: none;
      resize: none;
      padding: 24px;
      &::placeholder {
        color: var(--text-muted);
      }
    }

    > .delete-icon {
      flex-shrink: 0;
      cursor: pointer;
      & svg path {
        transition: var(--transition-normal);
      }
      &:hover svg path {
        stroke: var(--destructive);
      }
    }
  }

  .actions {
    display: flex;
    align-self: flex-end;
    gap: 24px;

    > button {
      display: block;
      height: 48px;
      padding: 12px 24px;
      border-radius: 8px;
      font-size: var(--text-sm);
      font-weight: var(--font-weight-bold);
      border: none;
      transition: var(--transition-normal);
      opacity: ${({ isSaving }) => (isSaving ? 0.6 : 1)};
      cursor: ${({ isSaving }) => (isSaving ? 'not-allowed' : 'pointer')};
      &:hover:not(:disabled) {
        filter: brightness(1.2) saturate(1.2);
      }
      &.add-btn {
        background: transparent;
        border: 1px solid var(--primary);
        color: var(--primary);
      }
      &.save-btn {
        background: var(--primary);
        color: var(--background);
      }
      &:disabled {
        color: var(--text-muted);
        border: 1px solid var(--text-muted);
        background: transparent;
        cursor: not-allowed;
      }
    }
  }
`;

export default BrandKitExternalLinks;
